import React, { useEffect, useRef, useState } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { setToken } from '../utils/api';
import AuthLayout, {
  AuthField, EnterButton, IconAlert, IconCaps, IconCheck, IconEye, IconEyeOff,
} from '../components/AuthLayout';

const LOGIN_URL = 'https://dev.sliitleo.org/Backend/login.php';
// How long the crest's "roar" plays before we navigate away.
const ROAR_MS = 650;
const ID_PATTERN = /^[A-Za-z]{2}\d{6,}$/;

async function fetchWithTimeout(resource, options = {}) {
  const { timeout = 12000, ...rest } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    return await fetch(resource, { ...rest, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export default function Login() {
  usePageTitle('Sign in');

  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [shake, setShake] = useState(false);
  const [remembered, setRemembered] = useState(null); // { id, name, image }

  const idRef = useRef(null);
  const pwRef = useRef(null);
  const lionRef = useRef(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('user') || 'null');
      const savedId = localStorage.getItem('sliitid') || (saved && saved.SLIITID) || '';
      if (savedId) {
        setRemembered({
          id: savedId,
          name: saved?.name || 'Leo Member',
          image: saved?.image || '/user.png',
        });
        setId(savedId);
      }
    } catch {
      // Unreadable saved profile -- fall back to the plain ID field.
    }
  }, []);

  // Focus the right field on desktop only: on a phone, focusing on load
  // raises the keyboard straight away and hides the screen before it's seen.
  useEffect(() => {
    if (!window.matchMedia('(min-width: 900px)').matches) return;
    (remembered ? pwRef : idRef).current?.focus();
  }, [remembered]);

  const switchUser = () => {
    setRemembered(null);
    setId('');
    setPassword('');
    setError('');
    setTimeout(() => idRef.current?.focus(), 0);
  };

  const fail = (message) => {
    setError(message);
    setShake(true);
    lionRef.current?.shudder();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading || signedIn) return;

    const submitId = (remembered ? remembered.id : id).trim();
    if (!submitId || !password) {
      fail('Enter your SLIIT ID and password.');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const res = await fetchWithTimeout(LOGIN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ SLIITID: submitId, AccountPassword: password }),
        timeout: 12000,
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        fail('Unexpected server response. Please try again.');
        setLoading(false);
        return;
      }

      if (data?.success) {
        // login.php always uppercases the ID before looking it up and
        // echoes that canonical form back as data.SLIITID -- use it (not
        // whatever case the person typed) so every feature that reads
        // localStorage's `user.SLIITID`/`sliitid` (search, "verified by",
        // quiz scores, project creation, tickets, ...) agrees with what's
        // actually stored in MemberBasic, regardless of how the ID was typed.
        const canonicalId = String(data.SLIITID || submitId).trim().toUpperCase();
        const userData = {
          SLIITID: canonicalId,
          SLIITLeoID: data.SLIITLeoID ?? null,
          name: data.full_name ?? (remembered?.name || 'Leo Member'),
          address: data.address ?? '',
          contactno: data.contactno ?? '',
          role: data.role ?? '',
          image: data.ProfileLink ?? data.image ?? remembered?.image ?? '/user.png',
        };
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('sliitid', canonicalId);
        setToken(data.token);

        // Stay in the "busy" state through the redirect so the button never
        // flashes back to idle while the next page loads.
        setSignedIn(true);
        const dest = (userData.role || '').toLowerCase() === 'banned' ? '/restricted' : '/home';

        // Let the crest roar and the form dissolve before leaving -- skipped
        // entirely for people who asked for reduced motion.
        const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!calm) {
          setLeaving(true);
          lionRef.current?.roar();
          await new Promise((resolve) => setTimeout(resolve, ROAR_MS));
        }
        window.location.href = dest;
        return;
      }

      fail(data?.error || data?.message || 'Incorrect SLIIT ID or password.');
    } catch (err) {
      fail(err?.name === 'AbortError'
        ? 'The request timed out. Please try again.'
        : 'Could not reach the server. Please try again.');
    }
    setLoading(false);
  };

  const trackCaps = (e) => {
    if (typeof e.getModifierState === 'function') setCapsOn(e.getModifierState('CapsLock'));
  };

  const idLooksValid = ID_PATTERN.test(id.trim());
  const hasError = !!error;
  let i = 0; // stagger index for the entrance animation

  return (
    <AuthLayout lionRef={lionRef} leaving={leaving}>
      <h1 className="auth-title rise" style={{ '--i': i++ }}>
        {remembered ? 'Welcome back' : 'Welcome to the pride'}
      </h1>

      <form onSubmit={handleLogin} noValidate>
        {remembered && (
          <div className="who rise" style={{ '--i': i++ }}>
            <img
              src={remembered.image}
              alt=""
              onError={(e) => {
                if (!e.currentTarget.dataset.fb) {
                  e.currentTarget.dataset.fb = '1';
                  e.currentTarget.src = '/user.png';
                }
              }}
            />
            <div className="who-text">
              <div className="who-name">{remembered.name}</div>
              <div className="who-id">{remembered.id}</div>
            </div>
            <button type="button" className="who-switch" onClick={switchUser}>Switch</button>
          </div>
        )}

        <div
          className={`fields rise ${shake ? 'shake' : ''}`}
          style={{ '--i': i++ }}
          onAnimationEnd={(e) => { if (e.animationName === 'auth-shake') setShake(false); }}
        >
          {!remembered && (
            <AuthField
              id="sl-id"
              label="SLIIT ID"
              valid={idLooksValid && !hasError}
              invalid={hasError}
              trailing={idLooksValid && !hasError ? (
                <i className="fld-ok" aria-hidden="true"><IconCheck size={18} /></i>
              ) : null}
            >
              <input
                id="sl-id"
                ref={idRef}
                className="auth-input is-id"
                type="text"
                value={id}
                onChange={(e) => { setId(e.target.value); lionRef.current?.pulse(1); }}
                placeholder="IT00000000"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="text"
                aria-invalid={hasError}
              />
            </AuthField>
          )}

          <AuthField
            id="sl-pw"
            label="Password"
            invalid={hasError}
            side={(
              <>
                {capsOn && <span className="caps" role="status"><IconCaps />Caps Lock</span>}
                <a className="auth-link" href="/forgot-password">Forgot?</a>
              </>
            )}
            trailing={(
              <button
                type="button"
                className="fld-act"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
                aria-pressed={showPw}
              >
                {showPw ? <IconEyeOff /> : <IconEye />}
              </button>
            )}
          >
            <input
              id="sl-pw"
              ref={pwRef}
              className="auth-input"
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); lionRef.current?.pulse(1); }}
              onKeyDown={trackCaps}
              onKeyUp={trackCaps}
              onBlur={() => setCapsOn(false)}
              placeholder="••••••••"
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              aria-invalid={hasError}
            />
          </AuthField>
        </div>

        <div aria-live="polite">
          {error && (
            <div className="err" role="alert">
              <IconAlert />
              <span>{error}</span>
            </div>
          )}
        </div>

        <EnterButton
          className="rise"
          style={{ '--i': i++ }}
          label="Enter"
          busyLabel="Signing in"
          busy={loading}
          done={signedIn}
        />
      </form>

      <p className="alt rise" style={{ '--i': i++ }}>
        Not a member?
        <a href="https://members.sliitleo.org/">Join the club</a>
      </p>
    </AuthLayout>
  );
}
